import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useRpc, throwApiError } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

export interface HubspotTokens {
  access_token: string;
  refresh_token: string;
  token_expires_at: number;
  hubspot_portal_id: string;
}

function useExchangeToken() {
  const rpc = useRpc();
  return useMutation({
    mutationFn: async (data: { code: string; state: string }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.hubspot['exchange-token'].$post({
        json: data,
      });
      if (!res.ok) await throwApiError(res, 'HubSpot token exchange failed');
      return res.json() as Promise<HubspotTokens>;
    },
  });
}

function useHubspotConnect() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tokens: HubspotTokens) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.hubspot.connect.$post({
        json: tokens,
      });
      if (!res.ok) await throwApiError(res, 'Failed to store HubSpot connection');
      return res.json() as Promise<{
        id: string;
        provider: string;
        external_id: string;
        display_name: string;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
    },
  });
}

export function useBatchCreateHubspot() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      connectionId: string;
      objectTypes: string[];
      schedule?: string;
    }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.hubspot['batch-create'].$post({
        json: data,
      });
      if (!res.ok) await throwApiError(res, 'Failed to create HubSpot tables');
      return res.json() as Promise<{
        tables: Array<{ id: string; name: string }>;
      }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.connectors.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
    },
  });
}

export function useHubspotOAuthConnect() {
  const rpc = useRpc();
  const exchangeToken = useExchangeToken();
  const connectMutation = useHubspotConnect();

  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    return () => {
      popupRef.current?.close();
    };
  }, []);

  const startOAuth = useCallback(async () => {
    setError(null);

    try {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.connectors.hubspot['authorize-url'].$post();
      if (!res.ok) {
        await throwApiError(res, 'Failed to get authorization URL');
      }
      const { url } = (await res.json()) as { url: string };

      const popup = window.open(url, 'hubspot-oauth', 'width=600,height=700,scrollbars=yes');
      if (!popup) {
        throw new Error('Popup blocked — please allow popups for this site');
      }
      popupRef.current = popup;

      const result = await new Promise<{ code: string; state: string }>((resolve, reject) => {
        const handleMessage = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'hubspot-oauth-callback') return;

          window.removeEventListener('message', handleMessage);
          clearInterval(pollTimer);

          if (event.data.error) {
            reject(new Error(event.data.error));
          } else {
            resolve({ code: event.data.code, state: event.data.state });
          }
        };

        const pollTimer = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollTimer);
            window.removeEventListener('message', handleMessage);
            reject(new Error('Authorization window was closed'));
          }
        }, 500);

        window.addEventListener('message', handleMessage);
      });

      const tokens = await exchangeToken.mutateAsync(result);
      await connectMutation.mutateAsync(tokens);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'OAuth failed';
      if (msg !== 'Authorization window was closed') {
        setError(msg);
      }
    } finally {
      popupRef.current = null;
    }
  }, [rpc, exchangeToken, connectMutation]);

  return { error, startOAuth };
}
